/**
 * WEB-1217 — response-interceptor fallback for 403 EMAIL_NOT_VERIFIED.
 *
 * EmailVerifiedGuard (WEB-420) reads isEmailVerified from the JWT itself.
 * verifyEmailThunk (authSlice.ts) already reissues a fresh token right after
 * verification, so this fallback should rarely fire on the main path — it's
 * insurance for a token that's stale for some OTHER reason (verified in a
 * different tab/device, an old token from before this fix, etc.): the
 * client.ts response interceptor does one silent refresh + retry, and if
 * that STILL 403s, sends the user to /verify-email instead of leaving a
 * blank/broken screen.
 *
 * Split out of client.ts on purpose — same reasoning as refreshExemptPaths.ts
 * and tokenRefresh.ts: framework-free (no axios/antd import), so it stays
 * unit-testable without the ts-jest / import.meta.env friction client.ts has.
 */

// WEB-776 — AcceptInvite.tsx already has its own, more specific inline
// handling of EMAIL_NOT_VERIFIED (shows "confirm your email, then come back
// to accept the invite" instead of a hard page redirect). A global redirect
// here would silently override that screen — see WEB-1217 card's "Осторожно:
// тот же признак читают приглашения".
const FALLBACK_EXEMPT_PREFIXES = ['/invites'] as const;

export function isEmailNotVerifiedFallbackExemptUrl(url: string | undefined): boolean {
  if (!url) return false;
  return FALLBACK_EXEMPT_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/**
 * WEB-1848 — the same exemption, read by WHERE THE USER IS instead of which
 * request failed.
 *
 * The URL-based exemption above covers only the invite request itself. But
 * the invite screen is loaded together with the app's own bootstrap calls
 * (App.tsx fires GET /projects twice: once after /auth/me, once on the
 * isAuthenticated transition). Those siblings are NOT /invites, so they ran
 * the generic fallback and did `window.location.href = '/verify-email…'` —
 * i.e. a sibling request yanked the user off the invite screen, dropping the
 * invite context (that URL carries no ?redirect back to the invite).
 *
 * /verify-email is listed for a different reason: redirecting to
 * /verify-email while already on /verify-email is a page-reload loop, and
 * every lap spends one refresh-token rotation.
 *
 * WEB-2363 — the guest auth screens (/login, /register, /reset-password) are
 * listed too. A person with a live but unverified session (typo in the email
 * at sign-up, so the code never arrives) opening /login got yanked straight
 * back to /verify-email by the bootstrap siblings (GET /projects), so the
 * sign-in page was unreachable for the whole session lifetime (up to 7 days,
 * WEB-1091). Why an exemption and not "no bootstrap requests on guest pages":
 * the bootstrap lives in App.tsx and also serves the invite screen and the
 * multi-tab token sync; reshaping it would touch every cabinet start-up path
 * for the sake of three guest screens. These screens never need a verified
 * session, so leaving the user where they are is always correct there.
 * /reset-password is reached from /login ("forgot password") and would hit
 * the same loop.
 *
 * Cabinet routes are deliberately NOT listed — sending an unverified user
 * from a cabinet screen to /verify-email is exactly what WEB-1217 built.
 */
const FALLBACK_EXEMPT_LOCATION_PREFIXES = [
  '/invite',
  '/verify-email',
  '/login',
  '/register',
  '/reset-password',
] as const;

export function isEmailNotVerifiedFallbackExemptLocation(
  pathname: string | undefined,
): boolean {
  if (!pathname) return false;
  return FALLBACK_EXEMPT_LOCATION_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Decode the `email` claim from a JWT without verifying its signature —
 * same technique as multiTabTokenSync.ts's decodeSub. Never used for
 * authorization, only to pre-fill the /verify-email redirect target.
 */
export function decodeEmailClaim(token: string | null): string | null {
  if (!token) return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : null;
  } catch {
    return null;
  }
}

/**
 * Where to send the user when a request keeps failing with 403
 * EMAIL_NOT_VERIFIED even after a silent refresh. Prefers the verify-email
 * screen pre-filled with their address; falls back to /login when the
 * (stale) token can't be read at all.
 */
export function emailNotVerifiedRedirectUrl(token: string | null): string {
  const email = decodeEmailClaim(token);
  return email ? `/verify-email?email=${encodeURIComponent(email)}` : '/login';
}
